import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState, Avatar, fmtDateTime, timeAgo } from "@/components/common";
import { OutcomePanel, PField, ProspectDialog, ConvertDialog, ActivityList, MoreDetailsCard } from "@/components/ProspectTools";
import QuickFilters from "@/components/QuickFilters";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Phone, MessageSquare, SkipForward, PartyPopper, StickyNote, Sparkles, Pencil, ListChecks, CalendarPlus, UserCog, Search, X, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { callViaPhone } from "@/lib/phone";
import { LeadQueue } from "@/components/LeadQueue";
import { FieldCard, TONES } from "@/pages/CampaignDashboard";

const QF = [{ key: "status", label: "Statuses" }, { key: "campaign", label: "Campaigns" }, { key: "city", label: "Cities" }, { key: "course_interest", label: "Courses" }, { key: "exam", label: "Exams" }, { key: "source", label: "Sources" }, { key: "last_outcome", label: "Last Dispositions" }, { key: "tag", label: "Tags" }];

export default function CallingQueuePage() {
  const [mode] = useState(() => JSON.parse(sessionStorage.getItem("ohc_queue") || "null")?.mode === "leads" ? JSON.parse(sessionStorage.getItem("ohc_queue")) : null);
  useEffect(() => { if (mode) sessionStorage.removeItem("ohc_queue"); }, [mode]);
  return mode ? <LeadQueue ids={mode.ids || []} /> : <ProspectQueue />;
}

function ProspectQueue() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [queue, setQueue] = useState(null);
  const [idx, setIdx] = useState(0);
  const [calling, setCalling] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [timeline, setTimeline] = useState({ calls: [], activities: [], notes: [] });
  const [modal, setModal] = useState(null);
  const [term, setTerm] = useState(q);
  const [view, setView] = useState(null);

  const savedQueue = useRef(JSON.parse(sessionStorage.getItem("ohc_queue") || "null"));
  const reqId = useRef(0);
  const load = async () => {
    const my = ++reqId.current;
    const put = (rows) => { if (my === reqId.current) { setQueue(rows); setIdx(0); } };
    setQueue(null);
    const saved = !q && savedQueue.current;
    sessionStorage.removeItem("ohc_queue");
    if (saved?.campaign && !saved.ids) {
      const { data } = await api.get(`/prospects?campaign=${encodeURIComponent(saved.campaign)}`);
      put(data.filter((p) => !/converted|not interested|invalid|dnc|wrong/i.test(p.status || "")));
      return;
    }
    if (saved?.ids?.length) {
      try {
        const { data } = await api.post("/prospects/by-ids", { ids: saved.ids });
        put(data);
        return;
      } catch { toast.error("Could not load the filtered queue — showing your normal queue"); }
    }
    const { data } = await api.get(q ? `/prospects?search=${encodeURIComponent(q)}` : "/prospects/queue");
    put(data);
  };
  useEffect(() => { setTerm(q); load(); }, [q]); // eslint-disable-line

  const list = view ?? queue;
  const p = list?.[idx];
  const refreshTimeline = () => { if (p) api.get(`/prospects/${p.id}/timeline`).then(({ data }) => setTimeline(data)).catch(() => setTimeline({ calls: [], activities: [], notes: [] })); };
  useEffect(() => { refreshTimeline(); }, [p?.id]); // eslint-disable-line

  const doSearch = (e) => { e.preventDefault(); const t = term.trim(); setParams(t ? { q: t } : {}); };
  const clearSearch = () => { setTerm(""); setParams({}); };

  if (queue === null) return <Loading label={q ? "Searching prospects" : "Building your calling queue"} />;

  const next = () => { setCalling(false); if (idx + 1 < list.length) setIdx(idx + 1); else load(); };
  const updateLocal = (patch) => { if (!patch || !p) return; const pid = p.id; setQueue((qq) => qq.map((x) => (x.id === pid ? { ...x, ...patch } : x))); };
  const onFiltered = (v) => { setView(v); setIdx((i) => Math.min(i, Math.max((v?.length || 1) - 1, 0))); };
  const afterOutcome = (data) => { updateLocal(data); refreshTimeline(); if (!q) next(); };

  const searchBar = (
    <form onSubmit={doSearch} className="flex items-center gap-2 mb-4" data-testid="queue-search-form">
      <div className="relative flex-1 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search prospects by name or mobile" className="pl-9 bg-white" data-testid="queue-search-input" />
      </div>
      <Button type="submit" className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="queue-search-btn">Search</Button>
      {q && <Button type="button" variant="outline" onClick={clearSearch} data-testid="queue-search-clear"><X className="w-4 h-4 mr-1" /> Back to my queue</Button>}
    </form>
  );

  const subtitle = q ? `${queue.length} result${queue.length === 1 ? "" : "s"} for "${q}"` : `${queue.length} prospects in your queue`;

  return (
    <div>
      <PageHeader title="My Calling Queue" subtitle={subtitle} testid="queue-header" />
      {searchBar}
      <QuickFilters rows={queue} fields={QF} onFiltered={onFiltered} testid="queue" />
      {!p ? (
        <div className="bg-white rounded-xl border border-slate-200" data-testid="queue-empty">
          {q ? <EmptyState icon={Search} title="No prospects found" subtitle={`Nothing matches "${q}".`} />
             : <EmptyState icon={PartyPopper} title="Queue cleared!" subtitle="You've worked through all assigned prospects. Great job." />}
        </div>
      ) : (
        <div className="grid lg:grid-cols-[280px_1fr_340px] gap-4 items-start">
          <aside className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="queue-list-panel">
            <div className="px-4 py-2.5 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">{q ? "Search Results" : "Queue"} ({list.length}{list.length !== queue.length ? ` of ${queue.length}` : ""})</div>
            <div className="max-h-[70vh] overflow-y-auto p-2 space-y-1.5">
              {list.map((it, i) => (
                <button key={it.id} onClick={() => { setIdx(i); setCalling(false); }} data-testid={`queue-item-${i}`}
                  className={`w-full text-left p-2.5 rounded-lg border transition-colors ${i === idx ? "border-[#4F46E5] bg-indigo-50" : "border-slate-100 hover:bg-slate-50"}`}>
                  <div className="flex items-center gap-2">
                    <Avatar name={it.name} size={30} />
                    <div className="min-w-0 flex-1"><div className="font-semibold text-sm text-slate-800 truncate">{it.name}</div><div className="text-[11px] text-slate-400 font-mono">{it.mobile}</div></div>
                    {it.edited_fields && Object.keys(it.edited_fields).length > 0 && <span className="w-2 h-2 rounded-full bg-amber-400" title="Has edited fields" />}
                  </div>
                  <div className="mt-1.5"><StatusBadge value={it.status} /></div>
                </button>
              ))}
            </div>
          </aside>

          <div>
            <div className="text-center text-xs text-slate-400 mb-3">{q ? "Result" : "Prospect"} {idx + 1} of {list.length}</div>
            <div className="bg-gradient-to-br from-[#312E81] to-[#4338CA] rounded-2xl p-6 sm:p-8 text-white shadow-xl border border-white/10" data-testid="queue-card">
              <div className="flex items-start justify-between">
                <div><div className="font-heading text-2xl sm:text-3xl font-extrabold" data-testid="queue-card-name">{p.name}</div><div className="text-indigo-100 font-mono mt-1 text-lg sm:text-xl font-semibold tracking-wide" data-testid="queue-card-mobile">{p.mobile}</div></div>
                <StatusBadge value={p.status} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
                {[["Interest", p.course_interest || "—"], ["Exam", p.exam || "—"], ["Rank", p.rank || "—"], ["Location", p.city || "—"]].map(([l, v]) => (
                  <div key={l}><div className="text-[11px] uppercase tracking-wider text-slate-400">{l}</div><div className="font-semibold mt-0.5">{v}</div></div>
                ))}
              </div>
              <div className="mt-2 text-xs text-slate-400">Campaign: {p.campaign || "—"} · Attempts: {p.attempts}{p.last_outcome ? ` · Last outcome: ${p.last_outcome}` : ""}{p.tag && <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-300 text-[#312E81] font-bold" data-testid="queue-card-tag">{p.tag}</span>}</div>
              <div className="flex flex-wrap gap-2 mt-6">
                <Button onClick={async () => { const r = await callViaPhone({ phone: p.mobile, name: p.name, entity_type: "prospect", entity_id: p.id }); setCalling(r !== "none"); }} className="bg-[#4F46E5] hover:bg-[#4338CA] flex-1 min-w-[120px] h-12 text-base" data-testid="queue-call-btn"><Phone className="w-5 h-5 mr-2" /> {calling ? "On Call…" : "Call"}</Button>
                <Button variant="secondary" className="bg-white/10 hover:bg-white/20 text-white border-0 h-12" data-testid="queue-wa-btn" onClick={() => setModal("wa")}><MessageSquare className="w-5 h-5" /></Button>
                <Button variant="secondary" className="bg-white/10 hover:bg-white/20 text-white border-0 h-12" data-testid="queue-sms-btn" onClick={() => setModal("sms")}><MessageCircle className="w-5 h-5" /></Button>
                {!q && <Button variant="secondary" className="bg-white/10 hover:bg-white/20 text-white border-0 h-12" data-testid="queue-skip-btn" onClick={next}><SkipForward className="w-5 h-5" /></Button>}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5 mt-4 shadow-sm">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Call Outcome</div>
              <OutcomePanel prospect={p} calling={calling} onSaved={afterOutcome} onInterested={(d) => { updateLocal(d); refreshTimeline(); setConvertOpen(true); }} />
            </div>
          </div>

          <aside className="rounded-xl border-l-4 border-[#6366F1] shadow-sm overflow-hidden bg-gradient-to-b from-indigo-50 via-white to-amber-50/40" data-testid="queue-lead-panel">
            <div className="p-4 bg-gradient-to-br from-[#312E81] via-[#4338CA] to-[#6366F1] text-white flex items-center gap-2">
              <div className="ring-2 ring-white/60 rounded-full"><Avatar name={p.name} size={40} /></div><div className="flex-1 min-w-0"><div className="font-heading font-bold truncate">{p.name}</div><div className="text-[11px] text-indigo-100">{p.mobile} · {p.city || "—"}</div><span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-amber-300 text-[#312E81] text-[10px] font-bold" data-testid="queue-sel-status">{p.status || "New"}</span></div>
              <button onClick={() => setModal("edit")} className="w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 text-white flex items-center justify-center" title="Edit" data-testid="queue-edit-btn"><Pencil className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-4 gap-1.5 p-3 border-b border-indigo-100">
              {[["task", ListChecks, "Add Task"], ["fu", CalendarPlus, "Follow-up"], ["assign", UserCog, "Assignee"], ["note", StickyNote, "Note"]].map(([m, Icon, l], i) => (
                <button key={m} onClick={() => setModal(m)} className={`flex flex-col items-center gap-1 py-2 rounded-lg transition-transform hover:-translate-y-0.5 ${TONES[i % TONES.length]}`} data-testid={`queue-${m}-btn`}><Icon className="w-4 h-4" /><span className="text-[10px] font-semibold">{l}</span></button>
              ))}
            </div>
            <div className="p-3 space-y-2 text-xs" data-testid="queue-lead-fields">
              <FieldCard title="Contact" tone="border-sky-400 bg-sky-50/70 text-sky-700">
                <PField k="Name" field="name" p={p} /><PField k="Phone" field="mobile" p={p} />
                <PField k="Alt Phone" field="alt_mobile" p={p} /><PField k="Email" field="email" p={p} />
                <PField k="Language" field="language" p={p} /><PField k="City" field="city" p={p} />
                <PField k="State" field="state" p={p} /><PField k="Source" field="source" p={p} />
              </FieldCard>
              <FieldCard title="Academic" tone="border-emerald-400 bg-emerald-50/70 text-emerald-700" onEdit={() => setModal("edit")} testid="queue-academic-edit">
                <PField k="Exam" field="exam" p={p} /><PField k="Rank" field="rank" p={p} />
                <PField k="Interested Course" field="course_interest" p={p} /><PField k="Year" field="year" p={p} />
                <PField k="12th %" field="twelfth_pct" p={p} /><PField k="12th Stream" field="stream" p={p} />
                {(p.course_pursued || p.ug_aggregate) && <><PField k="Course Pursued" field="course_pursued" p={p} /><PField k="Aggregate" field="ug_aggregate" p={p} /></>}
              </FieldCard>
              <FieldCard title="Pipeline" tone="border-amber-400 bg-amber-50/80 text-amber-700">
                <PField k="Campaign" field="campaign" p={p} /><PField k="Assignee" field="assigned_name" p={p} />
                <PField k="Attempts" v={p.attempts} /><PField k="Last Call" v={p.last_call_at ? timeAgo(p.last_call_at) : "—"} />
                <PField k="Next Action" v={p.next_action_at ? fmtDateTime(p.next_action_at) : "—"} /><PField k="Last Outcome" v={p.last_outcome} />
              </FieldCard>
              <MoreDetailsCard extra={p.extra} />
            </div>
            <div className="p-3">
              <div className="rounded-xl border-l-4 border-violet-400 bg-violet-50/70 p-3">
                <div className="text-xs font-bold text-violet-700 uppercase tracking-wider mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Activity History</div>
                <div className="space-y-2 h-72 overflow-y-scroll pr-1" data-testid="queue-activity"><ActivityList timeline={timeline} /></div>
              </div>
            </div>
          </aside>
        </div>
      )}

      <ConvertDialog prospect={p} open={convertOpen} onOpenChange={setConvertOpen} onDone={() => { if (q) load(); else next(); }} />
      <ProspectDialog mode={modal} prospect={p} onClose={() => setModal(null)} onSaved={(d) => { updateLocal(d); refreshTimeline(); }} />
    </div>
  );
}
